'use client'

import { useEffect, useRef } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

type UseModalParams = {
  isOpen: boolean
  onClose: () => void
}

/**
 * Concentra o comportamento esperado de um diálogo modal: prende o foco
 * enquanto aberto, devolve o foco ao elemento de origem ao fechar, fecha com
 * Escape e com o botão voltar do navegador, e impede a rolagem do fundo.
 */
export function useModal({ isOpen, onClose }: UseModalParams) {
  const modalRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const onCloseRef = useRef(onClose)
  const hasPushedState = useRef(false)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!isOpen) return

    previouslyFocused.current = document.activeElement as HTMLElement | null
    modalRef.current?.focus()

    window.history.pushState({ modalOpen: true }, '')
    hasPushedState.current = true

    const handlePopState = () => {
      hasPushedState.current = false
      onCloseRef.current()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current()
        return
      }

      if (event.key !== 'Tab') return

      const modal = modalRef.current
      if (!modal) return

      const focusables = Array.from(
        modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((element) => element.offsetParent !== null)

      if (focusables.length === 0) {
        event.preventDefault()
        modal.focus()
        return
      }

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement

      if (event.shiftKey && (active === first || active === modal)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('popstate', handlePopState)
    document.addEventListener('keydown', handleKeyDown)

    const previousOverflow = document.body.style.overflow
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth
    const previousPaddingRight = document.body.style.paddingRight

    document.body.style.overflow = 'hidden'
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    return () => {
      window.removeEventListener('popstate', handlePopState)
      document.removeEventListener('keydown', handleKeyDown)

      document.body.style.overflow = previousOverflow
      document.body.style.paddingRight = previousPaddingRight

      if (hasPushedState.current) {
        hasPushedState.current = false
        window.history.back()
      }

      previouslyFocused.current?.focus()
    }
  }, [isOpen])

  return { modalRef }
}
